import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { VehicleApi, Vehicle, NewVehicle} from '../models';
import { mapVehicleApiToVehicle } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';

import { API_FLEET_URL } from '@/utils';
import { RootState } from '@/store/store';

export interface VehicleFilter {
  description?: string;
  plate?: string;
  type?: string;
}

const vehiclesService = createApi({
  reducerPath: 'vehiclesService',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_FLEET_URL}/vehicles`,
          prepareHeaders: (headers, { getState }) => {
            const state = getState() as RootState;
      
            const token = state.auth?.auth?.accessToken || null;
            if (token) {
              headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
          }, }),
  tagTypes: ['Vehicle', 'Vehicles'],
  endpoints: (builder) => ({
    getVehicle: builder.query<Vehicle, { vehicleId: string }>({
      query: ({ vehicleId }) => ({
        url: `/${vehicleId}`,
      }),
      transformResponse: (response: VehicleApi) => mapVehicleApiToVehicle(response),
      providesTags: ['Vehicle'],
    }),
    getVehicles: builder.query<PaginatedResponse<Vehicle>, { filters?: VehicleFilter; paginated: PaginatedRequest; }>({
      query: ({ filters, paginated }) => ({
        url: '',
        params: {
          description: filters?.description,
          plateNumber: filters?.plate,
          vehicleTypeId: filters?.type,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<VehicleApi>) => ({
        ...response,
        items: response.items.map(mapVehicleApiToVehicle),
      }),
      providesTags: ['Vehicles'],
    }),
    createVehicle: builder.mutation<Vehicle, NewVehicle>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Vehicles'],
    }),
    updateVehicle: builder.mutation<Vehicle, { vehicleId: string; data: Partial<Vehicle> }>({
      query: ({ vehicleId, data }) => ({
        url: `/${vehicleId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Vehicle', 'Vehicles'],
    }),
    deleteVehicle: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicle', 'Vehicles'],
    }),
  }),
});

export default vehiclesService;

export const {
  useGetVehicleQuery,
  useGetVehiclesQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleMutation,
} = vehiclesService;
